import React from 'react';
import { useDispatch } from 'react-redux';
import { type Moment } from 'moment';
import { FormattedDate, FormattedMessage } from 'react-intl';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Icon, showConfirmCancelDialog, useSettings, Utils } from 'app/main/components';

interface CreditsafeReportsDownloadDialogProps {
	/** The Company uid */
	compUid: string;
	/** The Company  Creditsafe id */
	creditsafeId: string;
	/** Function used to refresh the page */
	updateRefresh: VoidFunction;
}

export const showCreditsafeReportsDownloadDialog = (props: CreditsafeReportsDownloadDialogProps) =>
	openDialog({
		children: <CreditsafeReportsDownloadDialog {...props} />,
		maxWidth: 'xs',
		fullWidth: true
	});

/**  */
export default function CreditsafeReportsDownloadDialog(props: CreditsafeReportsDownloadDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [planDetails, setPlanDetails] = React.useState<any>({});
	const [lastImportDate, setLastImportDate] = React.useState<Moment | undefined>();

	function showUpdateCompanyDialog({ forceRefresh }) {
		dispatch(
			showConfirmCancelDialog({
				title: t('CREDITSAFE_DIALOG_UPDATE_COMPANY_TITLE'),
				content: (
					<ul className="list-disc pl-24">
						<li>{t('DETAILS_COMPANIES_FIELD_TITLE')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_TAXCODE')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_CITY')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_ZIP')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_DISTRICT')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_REGION')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_COUNTRY')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_PEC')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ATECO')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_EMPLOYEES')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_REVENUE')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ESTABLISHMENT_DATE')}</li>
					</ul>
				),
				onCancel: () => downloadFinancialStatements({ forceRefresh, updateCompany: false }),
				onConfirm: () => downloadFinancialStatements({ forceRefresh, updateCompany: true }),
				dialogProps: { maxWidth: 'xs', fullWidth: true }
			})
		);
	}

	function downloadFinancialStatements({ forceRefresh, updateCompany }) {
		dispatch(Utils.alertProgressMsg(t));
		Utils.executeApi.post({
			url: `/api/creditsafe/companies/${props.compUid}?updateCompany=${updateCompany}&forceRefresh=${forceRefresh}`,
			dispatch,
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				dispatch(closeDialog());
				props.updateRefresh();
			}
		});
	}

	React.useEffect(() => {
		Utils.executeApi.get({
			url: '/api/creditsafe/account',
			dispatch,
			successHandler: response => setPlanDetails(response.data?.countryAccess.creditsafeConnectOnlineReports[0])
		});
	}, [dispatch]);

	React.useEffect(() => {
		Utils.executeStored({
			action: 'ext_st_creditsafe_financialstatements_check_latest',
			params: { comp_creditsafe_id: props.creditsafeId },
			dispatch,
			successHandler: response => setLastImportDate(Utils.parseDate(response.data.last_d_import))
		});
	}, [dispatch, props.creditsafeId]);

	return (
		<>
			<DialogTitle>{t('CREDITSAFE_DIALOG_DOWNLOAD_TITLE')}</DialogTitle>
			<DialogContent className="pb-0">
				<Typography className="mb-8">{t('CREDITSAFE_DIALOG_DOWNLOAD_DESCRIPTION')}</Typography>
				<List dense disablePadding>
					<ListItem disablePadding>
						<ListItemButton
							onClick={() => showUpdateCompanyDialog({ forceRefresh: true })}
							disabled={planDetails.used >= planDetails.paid}
						>
							<ListItemIcon>
								<Icon icon="cloud-download" size={32} />
							</ListItemIcon>
							<ListItemText
								primary={t('CREDITSAFE_DIALOG_DOWNLOAD_OPTION_CREDITSAFE')}
								secondary={
									<FormattedMessage
										id="creditsafeDownloadReportsDialog.credits"
										defaultMessage={t('SETTINGS_CREDITSAFE_CREDITS')}
										values={{ value: planDetails.used || '-', total: planDetails.paid || '-' }}
									/>
								}
							/>
						</ListItemButton>
					</ListItem>
					<ListItem disablePadding>
						<ListItemButton
							onClick={() => showUpdateCompanyDialog({ forceRefresh: false })}
							disabled={!lastImportDate}
						>
							<ListItemIcon>
								<Icon icon="server" size={32} />
							</ListItemIcon>
							<ListItemText
								primary={t('CREDITSAFE_DIALOG_DOWNLOAD_OPTION_LOCAL')}
								secondary={
									<FormattedMessage
										id="creditsafeDownloadReportsDialog.lastImportDate"
										defaultMessage={t('COMPANIES_CREDITSAFE_LAST_UPDATE')}
										values={{
											date: lastImportDate ? (
												<FormattedDate value={lastImportDate?.toDate()} />
											) : (
												'N/A'
											)
										}}
									/>
								}
							/>
						</ListItemButton>
					</ListItem>
				</List>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
			</DialogActions>
		</>
	);
}
